import '../App.css'
import {
	Text, Container, Space, createStyles, rem, Group, Card,
	Anchor, Divider, Stack, Button, Center
} from '@mantine/core'
import Footer from '../components/Footer';
import Head from '../components/Head';
import {
	IconHeadset, IconBrandInstagram, IconBrandTiktok,
	IconMail, IconBrandX
} from '@tabler/icons-react'

const useStyles = createStyles((theme) => ({

	heroWrapper: {
		background: 'radial-gradient(ellipse at 50% 60%, #d6f5f5 0%, #e8f6fb 50%, #f4fbff 100%)',
		paddingTop: rem(32),
		paddingBottom: rem(36),
		textAlign: 'center',
	},

	pageTitle: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 800,
		letterSpacing: '-0.02em',
		fontSize: rem(60),
		color: '#0d6a7a',

		[theme.fn.smallerThan('md')]: { fontSize: rem(44) },
		[theme.fn.smallerThan('sm')]: { fontSize: rem(34) },
	},

	dividerLabel: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 700,
		fontSize: rem(12),
		letterSpacing: '0.08em',
		textTransform: 'uppercase' as const,
		color: theme.colors.gray[5],
	},

	sectionDivider: {
		margin: `${rem(32)} 0 ${rem(24)}`,
	},

	// ── Email callout ──────────────────────────────────────────────────────
	emailCard: {
		background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
		border: `2px solid #2b9eb3`,
		borderRadius: theme.radius.lg,
		padding: `${rem(24)} ${rem(32)}`,
		textAlign: 'center',
		transition: 'box-shadow 0.2s ease, transform 0.2s ease',

		'&:hover': {
			boxShadow: theme.shadows.md,
			transform: 'translateY(-2px)',
		},
	},

	emailLabel: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 700,
		fontSize: rem(13),
		letterSpacing: '0.07em',
		textTransform: 'uppercase' as const,
		color: '#1a6e7d',
		marginBottom: rem(6),
	},

	emailAddress: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 700,
		fontSize: rem(20),
		color: '#0d6a7a',

		[theme.fn.smallerThan('sm')]: { fontSize: rem(16) },
	},

	// ── Social link cards ─────────────────────────────────────────────────
	socialCard: {
		transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
		cursor: 'pointer',

		'&:hover': {
			boxShadow: theme.shadows.md,
			transform: 'translateY(-3px)',
		},
	},

	socialLabel: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 700,
		fontSize: rem(15),
	},

	socialHandle: {
		fontSize: rem(13),
		color: theme.colors.gray[5],
	},
}))

interface SocialLink {
	icon: React.ReactNode
	platform: string
	handle: string
	url: string
	color: string
}

function Support() {
	const { classes } = useStyles()

	const links = [
		{ "link": "/About", "label": "About" },
		{ "link": "/Support", "label": "Support" },
		{ "link": "/Pp", "label": "Privacy" },
		{ "link": "/Tos", "label": "Terms of Use" },
		{ "link": "/Changelog", "label": "Changelog" },
	]

	const socials: SocialLink[] = [
		{
			icon: <IconBrandInstagram size={28} />,
			platform: 'Instagram',
			handle: '@getcomedyseen',
			url: 'https://www.instagram.com/getcomedyseen',
			color: '#c13584',
		}

	]

	return (
		<body>
			<Head />

			{/* ── Hero ── */}
			<div className={classes.heroWrapper}>
				<Group position="center" spacing="xs" mb={rem(8)}>
					<IconHeadset size={44} color="#0d6a7a" />
				</Group>
				<Text className={classes.pageTitle}>Support</Text>
				<Text size="lg" color="dimmed" mt={6}>
					We're here to help — reach out any time
				</Text>
			</div>

			<Container size="sm" py="xl">

				{/* ── Email callout ── */}
				<Divider
					className={classes.sectionDivider}
					label={<span className={classes.dividerLabel}>Email us</span>}
					labelPosition="center"
					size="xs"
				/>

				<div className={classes.emailCard}>
					<Group position="center" spacing="xs" mb="xs">
						<IconMail size={22} color="#0d6a7a" />
						<Text className={classes.emailLabel}>Drop us a line</Text>
					</Group>
					<Anchor href="mailto:info@getcomedyseen.com" underline={false}>
						<Text className={classes.emailAddress}>
							info@getcomedyseen.com
						</Text>
					</Anchor>
					<Text size="xs" color="dimmed" mt={8}>
						We aim to reply within 1–2 business days
					</Text>
				</div>

				{/* ── Social links ── */}
				<Divider
					className={classes.sectionDivider}
					label={<span className={classes.dividerLabel}>DM us on socials</span>}
					labelPosition="center"
					size="xs"
				/>

				<Stack spacing="sm">
					{socials.map(({ icon, platform, handle, url, color }) => (
						<Card
							key={platform}
							className={classes.socialCard}
							component="a"
							href={url}
							target="_blank"
							rel="noopener noreferrer"
							padding="md"
							radius="lg"
							withBorder
							shadow="xs"
							sx={{ borderLeft: `4px solid ${color}` }}
						>
							<Group>
								<span style={{ color }}>{icon}</span>
								<div>
									<Text className={classes.socialLabel}>{platform}</Text>
									<Text className={classes.socialHandle}>{handle}</Text>
								</div>
							</Group>
						</Card>
					))}
				</Stack>

				{/* ── Discovery call CTA ── */}
				<Divider
					className={classes.sectionDivider}
					label={<span className={classes.dividerLabel}>Want a chat?</span>}
					labelPosition="center"
					size="xs"
				/>

				<Center>
					<Stack spacing={6} align="center">
						<Text size="sm" color="dimmed" ta="center">
							Prefer to talk? Book a no-pressure discovery call with Pete.
						</Text>
						<Button
							component="a"
							href="https://cal.com/pete-comedy-seen/discovery"
							target="_blank"
							color="teal"
							size="md"
							sx={{
								fontFamily: "'Outfit', sans-serif",
								fontWeight: 700,
								transition: 'transform 0.2s ease, box-shadow 0.2s ease',
								'&:hover': {
									transform: 'translateY(-2px)',
									boxShadow: '0 8px 24px rgba(43,158,179,0.3)',
								},
							}}
						>
							Book a discovery call
						</Button>
					</Stack>
				</Center>

				<Space h="xl" />
			</Container>

			<Footer links={links} />
		</body>
	)
}

export default Support;